import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PublicSlimHeader from '../../Common/PublicSlimHeader';
import { getPublicResults } from '../../../services/eventApi';
import EventLoadingState from '../Common/EventLoadingState';

const AWARD_LABELS = {
  GOLD: 'Gold',
  SILVER: 'Silver',
  BRONZE: 'Bronze',
  FINALIST: 'Finalist',
  WINNER: 'Winner',
};

const PublicEventResults = () => {
  const { eventId } = useParams();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [allowIndustryLinking, setAllowIndustryLinking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    setError('');
    getPublicResults(eventId)
      .then((res) => {
        setResults(res.data?.data || []);
        setAllowIndustryLinking(!!res.data?.allowIndustryLinking);
      })
      .catch((err) => {
        setResults([]);
        setError(err.response?.data?.message || 'Results are not available yet.');
      })
      .finally(() => setLoading(false));
  }, [eventId]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of results) {
      const key = row.category?.name || row.categoryName || 'General';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [results]);

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicSlimHeader title="Event Results" />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-gray-900">Published results</h1>
          <p className="text-sm text-gray-500 mt-1">
            Official winners and rankings for this event.
          </p>

          {loading ? (
            <EventLoadingState message="Loading results..." />
          ) : error ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mt-6">
              {error}
            </p>
          ) : !results.length ? (
            <p className="text-sm text-gray-500 text-center py-12 mt-4">
              No published results yet. Check back after the event organisers publish winners.
            </p>
          ) : (
            <div className="mt-8 space-y-8">
              {grouped.map(([categoryName, rows]) => (
                <section key={categoryName}>
                  <h2 className="text-sm font-bold text-indigo-700 uppercase tracking-wider mb-3">
                    {categoryName}
                  </h2>
                  <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
                    {rows.map((row) => (
                      <div key={row._id} className="px-4 py-3 flex items-start justify-between gap-4 bg-white">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">
                            {row.applicantName || '—'}
                          </p>
                          {allowIndustryLinking && (row.industryName || row.industry?.name) ? (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {row.industryName || row.industry?.name}
                            </p>
                          ) : null}
                          {row.roundName && (
                            <p className="text-xs text-gray-400 mt-0.5">{row.roundName}</p>
                          )}
                          {row.publicRemarks && (
                            <p className="text-xs text-gray-600 mt-1 italic">{row.publicRemarks}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          {row.awardTier && (
                            <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 mb-1">
                              {AWARD_LABELS[row.awardTier] || row.awardTier}
                            </span>
                          )}
                          {row.rank != null && (
                            <p className="text-xs text-gray-500">Rank #{row.rank}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PublicEventResults;
